import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import ProtectedRoute from 'components/ProtectedRoute';
import RoleRoute from 'components/RoleRoute';

const DashboardPage = Loadable(lazy(() => import('pages/dashboard/DashboardPage')));

const UserList = Loadable(lazy(() => import('pages/users/UserList')));
const UserNew = Loadable(lazy(() => import('pages/users/UserNew')));
const UserInvite = Loadable(lazy(() => import('pages/users/UserInvite')));

const AuditLogPage = Loadable(lazy(() => import('pages/audit-log/AuditLogPage')));
const ProfilePage = Loadable(lazy(() => import('pages/profile/ProfilePage')));

const ClientList = Loadable(lazy(() => import('pages/clients/ClientList')));
const ClientDetail = Loadable(lazy(() => import('pages/clients/ClientDetail')));

const ProductList = Loadable(lazy(() => import('pages/products/ProductList')));
const ProductDetail = Loadable(lazy(() => import('pages/products/ProductDetail')));
const CalendarPage = Loadable(lazy(() => import('pages/calendar/CalendarPage')));
const SupplierList = Loadable(lazy(() => import('pages/suppliers/SupplierList')));
const SupplierDetail = Loadable(lazy(() => import('pages/suppliers/SupplierDetail')));
const ReceivedDocumentList = Loadable(lazy(() => import('pages/receivedDocuments/ReceivedDocumentList')));

const FiscalDocumentList = Loadable(lazy(() => import('pages/documents/FiscalDocumentList')));
const FiscalDocumentNew = Loadable(lazy(() => import('pages/documents/FiscalDocumentNew')));
const FiscalDocumentDetail = Loadable(lazy(() => import('pages/documents/FiscalDocumentDetail')));
const CollectionsQueue = Loadable(lazy(() => import('pages/collections/CollectionsQueue')));
const DealsBoard = Loadable(lazy(() => import('pages/deals/DealsBoard')));
const ForecastPage = Loadable(lazy(() => import('pages/insights/ForecastPage')));
const DealDetail = Loadable(lazy(() => import('pages/deals/DealDetail')));
const AutomationRulesPage = Loadable(lazy(() => import('pages/automation/AutomationRulesPage')));
const AutomationRuleEditor = Loadable(lazy(() => import('components/automation/AutomationRuleEditor')));

const TenantCreditsPage = Loadable(lazy(() => import('pages/credits/TenantCreditsPage')));
const ImportWizard = Loadable(lazy(() => import('pages/imports/ImportWizard')));
const InvoiceProviderSettingsPage = Loadable(lazy(() => import('pages/settings/InvoiceProviderSettingsPage')));
const WhatsAppSettingsPage = Loadable(lazy(() => import('pages/settings/WhatsAppSettingsPage')));
const EmailMailboxSettingsPage = Loadable(lazy(() => import('pages/settings/EmailMailboxSettingsPage')));
const EmailTemplatesPage = Loadable(lazy(() => import('pages/settings/EmailTemplatesPage')));
const CompanySettingsPage = Loadable(lazy(() => import('pages/settings/CompanySettingsPage')));
const RecordTypesSettingsPage = Loadable(lazy(() => import('pages/settings/RecordTypesSettingsPage')));
const BranchesSettingsPage = Loadable(lazy(() => import('pages/settings/BranchesSettingsPage')));
const SettingsLayout = Loadable(lazy(() => import('pages/settings/SettingsLayout')));
const SettingsIndexRedirect = Loadable(
  lazy(() => import('pages/settings/SettingsLayout').then((module) => ({ default: module.SettingsIndexRedirect })))
);

const MainRoutes = {
  path: '/',
  element: (
    <ProtectedRoute>
      <DashboardLayout />
    </ProtectedRoute>
  ),
  children: [
    { path: '/', element: <DashboardPage /> },
    { path: 'dashboard/default', element: <DashboardPage /> },
    { path: 'dashboard/:dashboardId', element: <DashboardPage /> },

    // Owner / Admin
    {
      path: 'users',
      element: (
        <RoleRoute roles={['owner', 'admin', 'superadmin']}>
          <UserList />
        </RoleRoute>
      )
    },
    {
      path: 'users/new',
      element: (
        <RoleRoute roles={['owner', 'admin', 'superadmin']}>
          <UserNew />
        </RoleRoute>
      )
    },
    {
      path: 'users/invite',
      element: (
        <RoleRoute roles={['owner', 'admin', 'superadmin']}>
          <UserInvite />
        </RoleRoute>
      )
    },

    // CRM — todos los roles con acceso
    { path: 'clients', element: <ClientList /> },
    { path: 'clients/:id', element: <ClientDetail /> },
    { path: 'products', element: <ProductList /> },
    { path: 'products/:id', element: <ProductDetail /> },
    { path: 'calendar', element: <CalendarPage /> },
    { path: 'suppliers', element: <SupplierList /> },
    { path: 'suppliers/:id', element: <SupplierDetail /> },
    { path: 'received-documents', element: <ReceivedDocumentList /> },
    { path: 'documents', element: <FiscalDocumentList /> },
    { path: 'documents/new', element: <FiscalDocumentNew /> },
    { path: 'documents/:id', element: <FiscalDocumentDetail /> },
    { path: 'deals', element: <DealsBoard /> },
    { path: 'insights/forecast', element: <ForecastPage /> },
    { path: 'deals/:id', element: <DealDetail /> },
    { path: 'collections', element: <CollectionsQueue /> },
    {
      path: 'automation',
      element: (
        <RoleRoute roles={['owner', 'admin', 'superadmin']}>
          <AutomationRulesPage />
        </RoleRoute>
      )
    },
    {
      path: 'automation/new',
      element: (
        <RoleRoute roles={['owner', 'admin', 'superadmin']}>
          <AutomationRuleEditor />
        </RoleRoute>
      )
    },
    {
      path: 'automation/:id',
      element: (
        <RoleRoute roles={['owner', 'admin', 'superadmin']}>
          <AutomationRuleEditor />
        </RoleRoute>
      )
    },

    // Compartidas
    {
      path: 'audit-log',
      element: (
        <RoleRoute roles={['owner', 'admin', 'superadmin']}>
          <AuditLogPage />
        </RoleRoute>
      )
    },
    { path: 'profile', element: <ProfilePage /> },

    // Owner / Admin — Import
    {
      path: 'imports',
      element: (
        <RoleRoute roles={['owner', 'admin', 'superadmin']}>
          <ImportWizard />
        </RoleRoute>
      )
    },

    // Owner credits (no platform admin screens)
    {
      path: 'settings',
      element: <SettingsLayout />,
      children: [
        { index: true, element: <SettingsIndexRedirect /> },
        { path: 'company', element: <CompanySettingsPage /> },
        { path: 'branches', element: <BranchesSettingsPage /> },
        { path: 'invoice-provider', element: <InvoiceProviderSettingsPage embedded /> },
        { path: 'whatsapp', element: <WhatsAppSettingsPage embedded /> },
        { path: 'credits', element: <TenantCreditsPage embedded /> },
        { path: 'email-mailbox', element: <EmailMailboxSettingsPage embedded /> },
        { path: 'email-templates', element: <EmailTemplatesPage /> },
        { path: 'record-types', element: <RecordTypesSettingsPage /> }
      ]
    },

    // Legacy redirects
    { path: 'credits', element: <Navigate to="/settings/credits" replace /> }
  ]
};

export default MainRoutes;
